import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useId } from 'react';
import { cn } from '@/lib/cn';
import { formatNumber } from '@/lib/format';

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: readonly number[];
  itemLabel?: string;
}

function pageList(current: number, totalPages: number): (number | 'gap')[] {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const pages = new Set([1, totalPages, current, current - 1, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((p) => pages.add(p));
  if (current >= totalPages - 2) [totalPages - 1, totalPages - 2, totalPages - 3].forEach((p) => pages.add(p));
  const sorted = [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const result: (number | 'gap')[] = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1]! > 1) result.push('gap');
    result.push(p);
  });
  return result;
}

const pageButton =
  'flex h-control-sm min-w-control-sm items-center justify-center rounded-md px-2 text-[13px] font-medium transition-colors focus-visible:shadow-focus focus-visible:outline-none disabled:pointer-events-none disabled:opacity-40';

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  itemLabel = 'results',
}: PaginationProps) {
  const sizeId = useId();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);

  return (
    <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-4 text-[13px] text-ink-secondary">
        <p aria-live="polite">
          Showing <span className="font-medium text-ink">{formatNumber(from)}</span>–
          <span className="font-medium text-ink">{formatNumber(to)}</span> of{' '}
          <span className="font-medium text-ink">{formatNumber(total)}</span> {itemLabel}
        </p>
        {onPageSizeChange && (
          <div className="hidden items-center gap-2 sm:flex">
            <label htmlFor={sizeId}>Rows per page</label>
            <select
              id={sizeId}
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="control h-control-sm w-auto py-0 pl-2 pr-7 text-[13px]"
            >
              {pageSizeOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <nav aria-label="Pagination" className="flex items-center gap-1">
        <button
          type="button"
          className={cn(pageButton, 'gap-1 text-ink-secondary hover:bg-surface-sunken')}
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          <ChevronLeft size={16} aria-hidden="true" />
          <span className="hidden sm:inline">Previous</span>
        </button>
        {pageList(page, totalPages).map((p, i) =>
          p === 'gap' ? (
            <span key={`gap-${i}`} aria-hidden="true" className="px-1 text-ink-muted">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              aria-label={`Page ${p}`}
              aria-current={p === page ? 'page' : undefined}
              className={cn(
                pageButton,
                p === page ? 'bg-primary text-primary-fg' : 'text-ink-secondary hover:bg-surface-sunken hover:text-ink',
              )}
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          className={cn(pageButton, 'gap-1 text-ink-secondary hover:bg-surface-sunken')}
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label="Next page"
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      </nav>
    </div>
  );
}
